import type { CatalogItem, EmploymentType, ExitReason, MailboxAction, RequestDetail, RequestItem } from '../api';

/** Working copy of a draft, held in the wizard and PATCHed back on a debounce. */
export type Draft = {
  employee: {
    fullName: string;
    idPassport: string;
    jobTitle: string;
    departmentId: string;
    managerName: string;
    startDate: string;
    employmentType: EmploymentType | '';
    contractEndDate: string;
    takingOverExistingAccount: boolean | null;
    takeoverAccountDetails: string;
    locationType: 'ONSITE' | 'OFFICE' | 'OTHER' | '';
    locationOther: string;
    contactNumber: string;
    emailRequired: boolean;
    emailAddress: string;

    // Offboarding. Held as strings like the rest of the draft: the wizard
    // talks in form values and draftToPatch does the narrowing.
    lastWorkingDay: string;
    exitReason: ExitReason | '';
    exitReasonOther: string;
    immediateExit: boolean;
    mailboxAction: MailboxAction | '';
    mailboxRetentionDays: string;
    outOfOfficeMessage: string;
    forwardToName: string;
    forwardUntil: string;
    releaseLicences: boolean;
    dataHandoverTo: string;
    sharedMailboxHandoverTo: string;
    teamsHandoverTo: string;
  };
  items: RequestItem[];
  specialInstructions: string;
};

export function draftFromDetail(detail: RequestDetail): Draft {
  const e = detail.employee;
  return {
    employee: {
      fullName: e?.fullName ?? '',
      idPassport: e?.idPassport ?? '',
      jobTitle: e?.jobTitle ?? '',
      departmentId: e?.departmentId ?? '',
      managerName: e?.managerName ?? '',
      startDate: e?.startDate ?? '',
      employmentType: e?.employmentType ?? '',
      contractEndDate: e?.contractEndDate ?? '',
      takingOverExistingAccount: e?.takingOverExistingAccount ?? null,
      takeoverAccountDetails: e?.takeoverAccountDetails ?? '',
      locationType: e?.locationType ?? '',
      locationOther: e?.locationOther ?? '',
      contactNumber: e?.contactNumber ?? '',
      emailRequired: e?.emailRequired ?? true,
      emailAddress: e?.emailAddress ?? '',

      lastWorkingDay: e?.lastWorkingDay ?? '',
      exitReason: e?.exitReason ?? '',
      exitReasonOther: e?.exitReasonOther ?? '',
      immediateExit: e?.immediateExit ?? false,
      mailboxAction: e?.mailboxAction ?? '',
      mailboxRetentionDays: e?.mailboxRetentionDays == null ? '' : String(e.mailboxRetentionDays),
      outOfOfficeMessage: e?.outOfOfficeMessage ?? '',
      forwardToName: e?.forwardToName ?? '',
      forwardUntil: e?.forwardUntil ?? '',
      releaseLicences: e?.releaseLicences ?? true,
      dataHandoverTo: e?.dataHandoverTo ?? '',
      sharedMailboxHandoverTo: e?.sharedMailboxHandoverTo ?? '',
      teamsHandoverTo: e?.teamsHandoverTo ?? '',
    },
    items: detail.items.map((item) => ({ ...item })),
    specialInstructions: detail.specialInstructions ?? '',
  };
}

/** Only send what the API accepts: empty strings become nulls so a cleared
 *  field actually clears rather than storing "". */
export function draftToPatch(draft: Draft) {
  const e = draft.employee;
  const orNull = (value: string) => (value.trim() === '' ? null : value.trim());

  return {
    employee: {
      fullName: e.fullName.trim(),
      idPassport: orNull(e.idPassport),
      jobTitle: orNull(e.jobTitle),
      departmentId: e.departmentId || null,
      managerName: orNull(e.managerName),
      startDate: e.startDate || null,
      employmentType: e.employmentType || null,
      contractEndDate: e.contractEndDate || null,
      takingOverExistingAccount: e.takingOverExistingAccount,
      takeoverAccountDetails: orNull(e.takeoverAccountDetails),
      locationType: e.locationType || null,
      locationOther: orNull(e.locationOther),
      contactNumber: orNull(e.contactNumber),
      emailRequired: e.emailRequired,
      emailAddress: orNull(e.emailAddress),

      lastWorkingDay: e.lastWorkingDay || null,
      exitReason: e.exitReason || null,
      exitReasonOther: orNull(e.exitReasonOther),
      immediateExit: e.immediateExit,
      mailboxAction: e.mailboxAction || null,
      mailboxRetentionDays: e.mailboxRetentionDays.trim() === '' ? null : Number(e.mailboxRetentionDays),
      outOfOfficeMessage: orNull(e.outOfOfficeMessage),
      forwardToName: orNull(e.forwardToName),
      forwardUntil: e.forwardUntil || null,
      releaseLicences: e.releaseLicences,
      dataHandoverTo: orNull(e.dataHandoverTo),
      sharedMailboxHandoverTo: orNull(e.sharedMailboxHandoverTo),
      teamsHandoverTo: orNull(e.teamsHandoverTo),
    },
    items: draft.items.map((item) => ({
      catalogItemId: item.catalogItemId,
      category: item.category,
      required: item.required,
      accessLevel: item.accessLevel,
      assetDetails: item.assetDetails,
      notes: item.notes,
      revoke: item.revoke ?? null,
    })),
    specialInstructions: orNull(draft.specialInstructions),
  };
}

// ---------------------------------------------------------------------------
// Item helpers: the wizard talks in "is this selected", the API in rows
// ---------------------------------------------------------------------------

export function isSelected(draft: Draft, catalogItemId: string): boolean {
  return draft.items.some((item) => item.catalogItemId === catalogItemId);
}

export function findItem(draft: Draft, catalogItemId: string): RequestItem | undefined {
  return draft.items.find((item) => item.catalogItemId === catalogItemId);
}

export function toggleItem(draft: Draft, entry: CatalogItem, on: boolean): Draft {
  if (!on) {
    // A compulsory item cannot be taken off; the server would put it back anyway.
    if (entry.isCompulsory) return draft;
    return { ...draft, items: draft.items.filter((item) => item.catalogItemId !== entry.id) };
  }
  if (isSelected(draft, entry.id)) return draft;

  return {
    ...draft,
    items: [
      ...draft.items,
      {
        catalogItemId: entry.id,
        category: entry.category,
        name: entry.name,
        legacyNumber: entry.legacyNumber,
        isCompulsory: entry.isCompulsory,
        required: true,
        // Read & write unless the catalogue says otherwise: it is what most
        // people need, and a wrong "read only" is the complaint IT hears most.
        accessLevel: entry.defaultAccessLevel ?? (entry.category === 'FILESHARE_FOLDER' ? 'RW' : null),
        assetDetails: null,
        notes: null,
        revoke: true,
        returnStatus: null,
        returnCondition: null,
      },
    ],
  };
}

export function updateItem(draft: Draft, catalogItemId: string, patch: Partial<RequestItem>): Draft {
  return {
    ...draft,
    items: draft.items.map((item) => (item.catalogItemId === catalogItemId ? { ...item, ...patch } : item)),
  };
}

/**
 * Mirrors the server's ensureCompulsoryItems on the local draft, so the
 * review step shows what the record will actually hold without a reload.
 * Returns the same draft when nothing is missing, so it is safe in an effect.
 */
export function ensureCompulsory(draft: Draft, entries: CatalogItem[]): Draft {
  let next = draft;
  for (const entry of entries) if (entry.isCompulsory) next = toggleItem(next, entry, true);
  return next;
}

export function setManyItems(draft: Draft, entries: CatalogItem[], on: boolean): Draft {
  let next = draft;
  for (const entry of entries) next = toggleItem(next, entry, on);
  return next;
}

export function countInCategory(draft: Draft, category: string): number {
  return draft.items.filter((item) => item.category === category).length;
}
